import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { gridClasses, type DataGridProps, type GridApi, type GridColDef, type GridValidRowModel } from '@mui/x-data-grid';

/** Column width mode. Default `auto` fits header/content; the first auto column fills leftover space. */
export type HFColumnSize = 'auto' | 'manual';

/** Floor for auto columns so a pathological squeeze degrades to horizontal scroll. */
export const MIN_AUTO_COLUMN_PX = 60;

export type HFGridColDef<R extends GridValidRowModel = GridValidRowModel> = GridColDef<R> & {
  /** Default `auto`. Set `manual` (or pass `width`/`flex`) to opt out of auto-fit. */
  hfSize?: HFColumnSize;
};

export type HeaderChromeOptions = {
  columnDrag?: boolean;
  columnSearch?: boolean;
  /** When true, no column-menu icon is shown. */
  disableColumnMenu?: boolean;
};

/**
 * Extra pixels reserved for header chrome so titles aren't clipped by icons.
 * Accounts for grip, search, always-visible sort, and menu icons (+ gaps/padding).
 * Calibrated against the rendered header: overestimating pushes the width
 * cascade into a lower tier (needless header ellipsis) at tight viewports.
 */
export function headerChromePx(opts: HeaderChromeOptions = {}): number {
  const { columnDrag = true, columnSearch = true, disableColumnMenu = false } = opts;
  return (
    20 + // header cell horizontal padding (10px per side)
    (columnDrag ? 18 : 0) + // drag grip + flex gap
    (columnSearch ? 22 : 0) + // magnifier button + flex gap
    20 + // sort icon (theme keeps it visible)
    (disableColumnMenu ? 0 : 17) // column menu (slightly overlaps the title container)
  );
}

export function isManualColumn<R extends GridValidRowModel>(col: HFGridColDef<R>): boolean {
  if (col.hfSize === 'manual') return true;
  if (col.hfSize === 'auto') return false;
  return col.width != null || col.flex != null;
}

/**
 * Apply default auto sizing: content-fit columns, first auto column `flex: 1`.
 * The flex is only a baseline for the pre-measurement render (and for grids with
 * `autosizeOnMount` off) — the width cascade replaces it with explicit widths.
 * Manual columns keep their `width` / `flex` / `minWidth` as requested, except
 * when `manualHeaderFit` reports the rendered header title would ellipsize:
 * then the width (and minWidth, so flex columns can't shrink back under it)
 * is raised to the measured no-ellipsis minimum.
 */
export function applyColumnSizing<R extends GridValidRowModel>(
  columns: Array<HFGridColDef<R>>,
  manualHeaderFit?: Record<string, number>,
): Array<GridColDef<R>> {
  const firstAutoIndex = columns.findIndex((col) => !isManualColumn(col));

  return columns.map((col, index) => {
    const { hfSize: _hfSize, ...rest } = col;
    if (isManualColumn(col)) {
      const fit = manualHeaderFit?.[col.field];
      if (!fit) return rest as GridColDef<R>;
      return {
        ...(rest as GridColDef<R>),
        minWidth: Math.max(rest.minWidth ?? 0, fit),
        width: rest.width != null ? Math.max(rest.width, fit) : undefined,
      };
    }
    const next: GridColDef<R> = {
      ...(rest as GridColDef<R>),
      // Kept low so the narrow-viewport tiers below can actually shrink the column;
      // an explicit consumer minWidth stays a hard floor.
      minWidth: rest.minWidth ?? MIN_AUTO_COLUMN_PX,
      width: undefined,
      flex: index === firstAutoIndex ? 1 : undefined,
    };
    return next;
  });
}

/** Fields sized by the auto-width cascade (everything not manual). */
export function autoSizeFields<R extends GridValidRowModel>(columns: Array<HFGridColDef<R>>): string[] {
  return columns.filter((col) => !isManualColumn(col)).map((col) => col.field);
}

export type AutoWidthMeasurement = {
  field: string;
  /** Minimal px with no ellipsis in the header nor in any measured cell. */
  ideal: number;
  /** Minimal px with no ellipsis in the header only. */
  headerMin: number;
  /** Hard floor — the column's `minWidth` (the grid clamps below it anyway). */
  min?: number;
};

/**
 * Width cascade for auto columns over the available viewport width:
 * 1. everything fits at `ideal` → each column gets ideal, the first absorbs the leftover;
 * 2. headers fit at `headerMin` → each column gets headerMin, leftover split
 *    proportionally to each column's content deficit (`ideal − headerMin`);
 * 3. otherwise → equal shares.
 * Every width is floored at `min`.
 */
export function distributeAutoWidths(measurements: AutoWidthMeasurement[], availablePx: number): Map<string, number> {
  const widths = new Map<string, number>();
  if (measurements.length === 0) return widths;

  const cols = measurements.map((m) => ({
    field: m.field,
    min: m.min ?? 0,
    ideal: Math.max(m.ideal, m.min ?? 0),
    headerMin: Math.max(m.headerMin, m.min ?? 0),
  }));

  const idealSum = cols.reduce((total, col) => total + col.ideal, 0);
  if (idealSum <= availablePx) {
    cols.forEach((col, index) => {
      widths.set(col.field, col.ideal + (index === 0 ? availablePx - idealSum : 0));
    });
    return widths;
  }

  const headerSum = cols.reduce((total, col) => total + col.headerMin, 0);
  if (headerSum <= availablePx) {
    const leftover = availablePx - headerSum;
    const deficitSum = cols.reduce((total, col) => total + (col.ideal - col.headerMin), 0);
    for (const col of cols) {
      const extra = deficitSum > 0 ? Math.floor((leftover * (col.ideal - col.headerMin)) / deficitSum) : 0;
      widths.set(col.field, col.headerMin + extra);
    }
    return widths;
  }

  const equal = Math.floor(availablePx / cols.length);
  for (const col of cols) {
    widths.set(col.field, Math.max(equal, col.min));
  }
  return widths;
}

/** Class on the ellipsizable title span of HF custom headers (ColumnSearchHeader / drag-only). */
export const HF_HEADER_TITLE_CLASS = 'hf-columnHeaderTitle';

const HEADER_TITLE_SELECTOR = `.${gridClasses.columnHeaderTitle}, .${HF_HEADER_TITLE_CLASS}`;

/**
 * Measure, from the live DOM, how wide each rendered header must be for its title
 * to lose the ellipsis. Exact per column — real icons, gaps and padding are already
 * in the layout, so the deficit is just `scrollWidth − clientWidth` of the title.
 * Returns entries only for clipped titles (nothing in jsdom, where layout is 0).
 */
export function measureHeaderFit(container: HTMLElement, fields: string[]): Map<string, number> {
  const fit = new Map<string, number>();
  for (const field of fields) {
    const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(field) : field;
    const header = container.querySelector<HTMLElement>(`.${gridClasses.columnHeader}[data-field="${escaped}"]`);
    const title = header?.querySelector<HTMLElement>(HEADER_TITLE_SELECTOR);
    if (!header || !title) continue;
    const deficit = title.scrollWidth - title.clientWidth;
    if (deficit <= 0) continue;
    // +2 buffer absorbs subpixel rounding so widths settle in one pass.
    fit.set(field, Math.ceil(header.getBoundingClientRect().width + deficit) + 2);
  }
  return fit;
}

/**
 * Measure header title text widths (px) with the grid's real header font.
 * Samples a rendered title for computed styles so theme overrides apply;
 * returns 0s when no layout is available (e.g. jsdom).
 */
export function measureHeaderTextPx(container: HTMLElement, titles: Array<{ field: string; text: string }>): Map<string, number> {
  const widths = new Map<string, number>();
  const probe = document.createElement('span');
  const sample = container.querySelector<HTMLElement>(`.${gridClasses.columnHeaderTitle}`);
  if (sample) {
    const style = window.getComputedStyle(sample);
    probe.style.fontFamily = style.fontFamily;
    probe.style.fontSize = style.fontSize;
    probe.style.fontWeight = style.fontWeight;
    probe.style.letterSpacing = style.letterSpacing;
    probe.style.textTransform = style.textTransform;
  }
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.whiteSpace = 'nowrap';
  document.body.appendChild(probe);
  for (const { field, text } of titles) {
    probe.textContent = text;
    widths.set(field, Math.ceil(probe.getBoundingClientRect().width));
  }
  probe.remove();
  return widths;
}

export type UseColumnSizingOptions<R extends GridValidRowModel> = {
  apiRef: RefObject<GridApi | null>;
  containerRef: RefObject<HTMLElement | null>;
  /** Columns in display order. */
  columns: Array<HFGridColDef<R>>;
  /** The `autosizeOnMount` prop — when false, explicit widths are kept untouched. */
  enabled: boolean;
  /**
   * Pause measuring while a drag live-reorders: widths are already settled and
   * re-running autosize on every swap makes columns jitter under the pointer.
   */
  paused: boolean;
  /** Header chrome px (grip / search / sort / menu icons) added to measured title text. */
  chromePx: number;
  autosizeOptions?: DataGridProps['autosizeOptions'];
  /** Visible rows — re-runs the auto cascade when the data changes. */
  rows: readonly R[];
};

/**
 * Live column sizing over `applyColumnSizing`:
 * - Manual columns keep their requested width unless the rendered header title
 *   ellipsizes; then width/minWidth is raised to the exact measured minimum.
 *   Measurements are monotonic (only grow toward each header's intrinsic
 *   minimum), so the measure → widen → re-measure loop settles instead of
 *   oscillating. Re-measures after each bump (a widened fixed column can shrink
 *   a flex sibling into a new deficit) and on viewport resizes.
 * - Auto columns get minimal no-ellipsis widths and the first auto column
 *   absorbs leftover space; when the viewport is too narrow, degrade per
 *   `distributeAutoWidths` (headers-only, then equal shares).
 */
export function useColumnSizing<R extends GridValidRowModel>({
  apiRef,
  containerRef,
  columns,
  enabled,
  paused,
  chromePx,
  autosizeOptions,
  rows,
}: UseColumnSizingOptions<R>): Array<GridColDef<R>> {
  // Measured no-ellipsis header minimums for manual columns (field → px).
  const [manualHeaderFit, setManualHeaderFit] = useState<Record<string, number>>({});

  const sizedColumns = useMemo(() => applyColumnSizing(columns, manualHeaderFit), [columns, manualHeaderFit]);

  const manualFields = useMemo(() => columns.filter((col) => isManualColumn(col)).map((col) => col.field), [columns]);
  useEffect(() => {
    if (!enabled || paused || manualFields.length === 0) return;

    const measure = () => {
      const container = containerRef.current;
      if (!container) return;
      const fit = measureHeaderFit(container, manualFields);
      if (fit.size === 0) return;
      setManualHeaderFit((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const [field, px] of fit) {
          if ((next[field] ?? 0) >= px) continue;
          next[field] = px;
          changed = true;
        }
        return changed ? next : prev;
      });
    };

    const id = window.setTimeout(measure, 0);
    let resizeId = 0;
    const unsubscribe = apiRef.current?.subscribeEvent('viewportInnerSizeChange', () => {
      window.clearTimeout(resizeId);
      resizeId = window.setTimeout(measure, 100);
    });
    return () => {
      window.clearTimeout(id);
      window.clearTimeout(resizeId);
      unsubscribe?.();
    };
  }, [apiRef, containerRef, enabled, paused, manualFields, manualHeaderFit]);

  const autoFields = useMemo(() => autoSizeFields(columns), [columns]);
  const lastViewportWidthRef = useRef(0);
  useEffect(() => {
    if (!enabled || paused || autoFields.length === 0) return;

    let cancelled = false;
    const apply = async () => {
      const api = apiRef.current;
      const container = containerRef.current;
      if (!api || !container) return;
      try {
        // Pass 1: minimal width fitting rendered cell contents (headers are
        // measured separately below — they carry icon chrome cells don't have).
        await api.autosizeColumns({
          columns: autoFields,
          includeHeaders: false,
          includeOutliers: false,
          expand: false,
          ...autosizeOptions,
        });
        if (cancelled) return;
        const headerTextPx = measureHeaderTextPx(
          container,
          autoFields.map((field) => ({ field, text: api.getColumn(field)?.headerName ?? field })),
        );
        // Header minimum = title text + chrome for the grip / search / sort /
        // menu icons around it; a column's ideal fits both header and content.
        const measurements = autoFields.map((field) => {
          const col = api.getColumn(field);
          const headerMin = (headerTextPx.get(field) ?? 0) + chromePx;
          return {
            field,
            ideal: Math.max(col?.computedWidth ?? 0, headerMin),
            headerMin,
            min: col?.minWidth,
          };
        });
        const viewportWidth = api.getRootDimensions()?.viewportInnerSize.width ?? 0;
        lastViewportWidthRef.current = viewportWidth;
        const fixedPx = api
          .getVisibleColumns()
          .filter((col) => !autoFields.includes(col.field))
          .reduce((total, col) => total + col.computedWidth, 0);
        const available = viewportWidth - fixedPx;
        if (available <= 0) return;
        for (const [field, width] of distributeAutoWidths(measurements, available)) {
          api.setColumnWidth(field, width);
        }
      } catch {
        // grid not ready
      }
    };

    const id = window.setTimeout(() => void apply(), 0);
    // The chosen tier depends on the viewport — redistribute on real width changes.
    let resizeId = 0;
    const unsubscribe = apiRef.current?.subscribeEvent('viewportInnerSizeChange', (size) => {
      if (size.width === lastViewportWidthRef.current) return;
      window.clearTimeout(resizeId);
      resizeId = window.setTimeout(() => void apply(), 100);
    });
    return () => {
      cancelled = true;
      window.clearTimeout(id);
      window.clearTimeout(resizeId);
      unsubscribe?.();
    };
    // `manualHeaderFit`: widened manual columns change the space left for auto columns.
  }, [apiRef, containerRef, enabled, paused, autoFields, autosizeOptions, rows, chromePx, manualHeaderFit]);

  return sizedColumns;
}
